import type { EngineEventHandler, EngineEventName, EngineEvents } from '../types';

/**
 * Tiny typed pub/sub bus. Zero allocations on hot emit paths beyond the
 * listener iteration itself; handlers are stored in insertion-ordered Sets.
 */
export class EventBus {
  private readonly listeners = new Map<EngineEventName, Set<EngineEventHandler<EngineEventName>>>();

  on<E extends EngineEventName>(event: E, handler: EngineEventHandler<E>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler as EngineEventHandler<EngineEventName>);
    return () => this.off(event, handler);
  }

  once<E extends EngineEventName>(event: E, handler: EngineEventHandler<E>): () => void {
    const unsubscribe = this.on(event, (payload) => {
      unsubscribe();
      handler(payload);
    });
    return unsubscribe;
  }

  off<E extends EngineEventName>(event: E, handler: EngineEventHandler<E>): void {
    const set = this.listeners.get(event);
    if (!set) return;
    set.delete(handler as EngineEventHandler<EngineEventName>);
    if (set.size === 0) {
      this.listeners.delete(event);
    }
  }

  emit<E extends EngineEventName>(event: E, payload: EngineEvents[E]): void {
    const set = this.listeners.get(event);
    if (!set || set.size === 0) return;
    // Snapshot iteration so handlers may unsubscribe mid-emit safely.
    for (const handler of [...set]) {
      (handler as EngineEventHandler<E>)(payload);
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}
